<template>
  <FlyMenu
    v-if="menuItems.length"
    :menu-items="menuItems"
    :popper-options="{
      placement: 'bottom-end',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 2],
          },
        },
      ],
    }"
    @menu-item-click="onMenuItemClick"
  >
    <slot></slot>
  </FlyMenu>
  <ArchivePipelineConfirmModal
    v-if="showArchiveConfirmModal"
    :pipeline="pipeline"
  />
</template>

<script>
import FlyMenu from 'shared/components/Menu/FlyMenu';
import { get, includes } from 'lodash';
import PIPELINE_STATUS_ENUM from 'web/pipelines/PIPELINE_STATUS_ENUM';
import VueTypes from 'vue-types';
import {
  CONFIRM_ARCHIVE_PIPELINE_FORM,
  CREATE_PIPELINE_FORM,
} from 'web/form/formKeys';
import {
  CONFIRM_ARCHIVE_PIPELINE_MODAL,
  CREATE_PIPELINE_MODAL,
} from 'web/modal/modalKeys';
import { changePipelineStatusAction } from 'web/pipelines/pipelinesActionTypes';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  clearFormData,
  setInitialFormData,
} from 'shared/stores/form/formMutationTypes';
import { setModalOpenState } from 'shared/store/modal/modalMutationTypes';
import { showToastAction } from 'shared/stores/toast/toastActionTypes';
import ArchivePipelineConfirmModal from 'web/alerts/ArchivePipelineConfirmModal';
import { isModalOpen } from 'shared/store/modal/modalGetterTypes';
import TOAST_TYPE_ENUM from 'shared/components/Toast/TOAST_TYPE_ENUM';

export default {
  name: 'PipelineActionMenu',
  components: {
    FlyMenu,
    ArchivePipelineConfirmModal,
  },
  props: {
    pipeline: VueTypes.object.isRequired,
  },
  computed: {
    ...mapGetters({
      isModalOpen,
    }),
    showArchiveConfirmModal() {
      return this.isModalOpen(CONFIRM_ARCHIVE_PIPELINE_MODAL + this.pipelineId);
    },
    pipelineName() {
      return get(this.pipeline, 'name');
    },
    pipelineId() {
      return get(this.pipeline, 'id');
    },
    pipelineStatus() {
      return get(this.pipeline, 'status') || null;
    },
    toastTitle() {
      return {
        start: {
          progress: 'Starting pipeline',
          success: 'Pipeline started',
        },
      };
    },
    menuItems() {
      const menuItems = [];

      if (!includes([PIPELINE_STATUS_ENUM.ARCHIVED], this.pipelineStatus)) {
        menuItems.push({
          icon: 'Edit',
          key: 'renamePipeline',
          text: 'Rename',
        });
      }

      if (includes([PIPELINE_STATUS_ENUM.RUNNING], this.pipelineStatus)) {
        menuItems.push({
          icon: 'Pause',
          key: 'stop',
          text: 'Stop Sync',
        });
      }

      if (
        !includes(
          [
            PIPELINE_STATUS_ENUM.DRAFT,
            PIPELINE_STATUS_ENUM.ARCHIVED,
            PIPELINE_STATUS_ENUM.RUNNING,
            PIPELINE_STATUS_ENUM.STARTING,
            PIPELINE_STATUS_ENUM.PAUSING,
          ],
          this.pipelineStatus
        )
      ) {
        menuItems.push({
          icon: 'Refresh',
          key: 'start',
          text: 'Start Sync',
        });
      }

      if (
        !includes(
          [
            PIPELINE_STATUS_ENUM.ARCHIVED,
            PIPELINE_STATUS_ENUM.STARTING,
            PIPELINE_STATUS_ENUM.PAUSING,
            PIPELINE_STATUS_ENUM.RUNNING,
          ],
          this.pipelineStatus
        )
      ) {
        menuItems.push({
          icon: 'Archive',
          key: 'archive',
          text: 'Archive',
        });
      }

      return menuItems;
    },
  },
  methods: {
    ...mapMutations({
      setInitialFormData,
      clearFormData,
      setModalOpenState,
    }),
    ...mapActions({
      changePipelineStatusAction,
      showToastAction,
    }),
    async onMenuItemClick({ key }) {
      switch (key) {
        case 'renamePipeline':
          this.clearFormData(CREATE_PIPELINE_FORM);
          this.setInitialFormData({
            key: CREATE_PIPELINE_FORM,
            formData: {
              id: this.pipelineId,
              name: this.pipelineName,
            },
          });
          this.setModalOpenState({
            key: CREATE_PIPELINE_MODAL,
            open: true,
          });
          break;
        case 'start':
          try {
            this.showToastAction({
              key: changePipelineStatusAction,
              title: this.toastTitle.start['progress'],
              type: TOAST_TYPE_ENUM.PROGRESS,
              duration: 0,
            });
            await this.changePipelineStatusAction({
              pipelineId: this.pipelineId,
              status: 'start',
            });
            this.showToastAction({
              key: changePipelineStatusAction,
              title: this.toastTitle.start['success'],
              type: TOAST_TYPE_ENUM.SUCCESS,
            });
          } catch (err) {
            this.showToastAction({
              key: changePipelineStatusAction,
              title: 'An error occurred',
              description:
                'Please try again or contact support if the issue persists.',
              type: TOAST_TYPE_ENUM.ERROR,
            });
          }
          break;
        case 'archive':
        case 'stop':
          this.clearFormData(CONFIRM_ARCHIVE_PIPELINE_FORM);
          this.setInitialFormData({
            key: CONFIRM_ARCHIVE_PIPELINE_FORM,
            formData: {
              id: this.pipelineId,
              key: key,
            },
          });
          this.setModalOpenState({
            key: CONFIRM_ARCHIVE_PIPELINE_MODAL + this.pipelineId,
            open: true,
          });
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss"></style>
